import type { ReactElement, SVGProps } from 'react';

export type ModuleIconId =
  | 'learn'
  | 'listen'
  | 'speak'
  | 'read'
  | 'write'
  | 'pinyin'
  | 'practice'
  | 'test'
  | 'story'
  | 'review'
  | 'path'
  | 'challenge';

function Svg({ children, ...props }: SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      {children}
    </svg>
  );
}

/** New words: a four-point sparkle. */
function SparkleIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <path d="M12 3v4M12 17v4M3 12h4M17 12h4M6.3 6.3l2.1 2.1M15.6 15.6l2.1 2.1M17.7 6.3l-2.1 2.1M8.4 15.6l-2.1 2.1" />
      <path d="M12 8l1.2 2.8L16 12l-2.8 1.2L12 16l-1.2-2.8L8 12l2.8-1.2z" />
    </Svg>
  );
}

/** Listen: headphones. */
function ListenIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <path d="M4 18v-6a8 8 0 0116 0v6" />
      <rect x="2" y="16" width="4" height="6" rx="2" />
      <rect x="18" y="16" width="4" height="6" rx="2" />
    </Svg>
  );
}

/** Speak: speech bubble with sound waves. */
function SpeakIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <path d="M3 5a2 2 0 012-2h11a2 2 0 012 2v8a2 2 0 01-2 2H9l-4 4v-4H5a2 2 0 01-2-2z" />
      <path d="M13 7c.8.6.8 3.4 0 4M16 5.5c1.6 1.2 1.6 6.8 0 8" />
    </Svg>
  );
}

/** Read: open book. */
function ReadIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <path d="M12 6c-2-2-5.5-2-9-1v13c3.5-1 7-1 9 1c2-2 5.5-2 9-1V5c-3.5-1-7-1-9 1z" />
      <path d="M12 6v13" />
    </Svg>
  );
}

/** Write: pen. */
function WriteIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4z" />
    </Svg>
  );
}

/** Practice: target. */
function PracticeIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="5" />
      <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
    </Svg>
  );
}

/** Story: closed book with a bookmark ribbon. */
function StoryIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <rect x="4" y="3" width="16" height="18" rx="1.5" />
      <path d="M9 3v6l3-2 3 2V3" />
    </Svg>
  );
}

/** Pinyin: the letter a with a tone mark above it. */
function PinyinIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <path d="M9 5l3 3 3-3" />
      <circle cx="11" cy="16" r="4" />
      <path d="M15 12v8" />
    </Svg>
  );
}

/** Test: clipboard with a tick. */
function TestIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <rect x="5" y="4" width="14" height="17" rx="2" />
      <path d="M9 4V3h6v1" />
      <path d="M9 13l2 2 4-4" />
    </Svg>
  );
}

/** Adventure path: winding trail with a flag at the end. */
function PathIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <path d="M4 20c0-4 4-4 4-8s-4-4-4-8" strokeDasharray="2 3" />
      <path d="M8 12c0 4 4 4 4 0s4-4 4 0 4 4 4 0" />
      <path d="M18 3v6M18 3h3l-1 1.5L21 6h-3" />
    </Svg>
  );
}

/** Lesson review: flag. */
function ReviewIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <path d="M5 21V4" />
      <path d="M5 4h13l-3 4 3 4H5" />
    </Svg>
  );
}

/** Daily challenge: a lightning bolt. */
function ChallengeIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <path d="M13 2 4 14h6l-1 8 9-12h-6z" fill="currentColor" stroke="none" />
    </Svg>
  );
}

/** Streak count: a filled flame with a lighter inner tongue. */
export function FlameIcon({ className = 'h-6 w-6 text-accent' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path
        fill="currentColor"
        d="M12.5 2.3c1.1 2.5-.6 4.1-1.9 5.7-1.1 1.4-1.8 2.7-1.8 4.3a3.2 3.2 0 006.4 0c0-1-.3-1.9-.8-2.6 1.9 1.5 3.4 3.8 3.4 6.5a6.2 6.2 0 11-12.4 0c0-3.7 1.9-5.9 3.7-7.9 1.5-1.7 2.8-3.1 3.4-6z"
      />
      <path
        fill="#FDECCB"
        d="M12 12.6c.6.9.9 1.7.9 2.7a1.9 1.9 0 11-3.8 0c0-.8.3-1.5.7-2 .3.6.7.9 1.1.4.3-.4.7-.7 1.1-1.1z"
      />
    </svg>
  );
}

/** Filled five-point star, used for daily star counts. */
export function StarIcon({ className = 'h-6 w-6 text-accent' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path
        fill="currentColor"
        d="M12 2.5l2.6 5.6 6.2.6-4.7 4.1 1.4 6.1L12 15.9l-5.5 3 1.4-6.1-4.7-4.1 6.2-.6z"
      />
    </svg>
  );
}

/** Microphone: capsule body with a stand. */
export function MicIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <rect x="9" y="2" width="6" height="12" rx="3" />
      <path d="M5 11a7 7 0 0014 0" />
      <path d="M12 18v4M8 22h8" />
    </Svg>
  );
}

/** Speaker: cone with sound waves. */
export function SpeakerIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <path d="M4 9v6h4l5 4V5L8 9z" />
      <path d="M17 9c1.2 1 1.2 5 0 6M19.5 7c2 2 2 8 0 10" />
    </Svg>
  );
}

/** Refresh: circular arrow. */
export function RefreshIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <path d="M4 12a8 8 0 0114-5.3M20 12a8 8 0 01-14 5.3" />
      <path d="M18 3v4h-4M6 21v-4h4" />
    </Svg>
  );
}

/** Play: triangle. */
export function PlayIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <path d="M7 4l13 8-13 8z" fill="currentColor" stroke="none" />
    </Svg>
  );
}

/** Check: tick mark. */
export function CheckIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <Svg {...props}>
      <path d="M4 12.5l5 5.5L20 6" />
    </Svg>
  );
}

const ICONS: Record<ModuleIconId, (props: SVGProps<SVGSVGElement>) => ReactElement> = {
  learn: SparkleIcon,
  listen: ListenIcon,
  speak: SpeakIcon,
  read: ReadIcon,
  write: WriteIcon,
  pinyin: PinyinIcon,
  practice: PracticeIcon,
  test: TestIcon,
  story: StoryIcon,
  review: ReviewIcon,
  path: PathIcon,
  challenge: ChallengeIcon,
};

export function ModuleIcon({
  id,
  className = 'h-10 w-10 text-brand',
}: {
  id: ModuleIconId;
  className?: string;
}) {
  const Icon = ICONS[id];
  return <Icon className={className} />;
}
